/******************************************************************************
 * CustomerDuplicateGroupRow : un groupe de doublons clients, résumé sur une
 * ligne et dépliable en sélecteur de fiche à conserver.
 *
 * Le regroupement se fait sur le nom et le numéro de TVA, donc un groupe peut
 * contenir un vrai négatif — deux sociétés d'un même groupe, par exemple. Chaque
 * membre non survivant porte donc une case « Inclure » : décochée, la fiche est
 * laissée tranquille pendant que le reste fusionne. Le back l'accepte déjà :
 * `sourceCustomerIds` est une liste explicite qu'il valide sur l'existence et
 * l'activité des fiches, pas contre le groupe détecté.
 *
 * La fusion est irréversible depuis l'écran (le back désactive les fiches au
 * lieu de les supprimer, mais rien ici ne les réactive), d'où le dialogue de
 * confirmation qui nomme ce qui va bouger — et l'avertissement supplémentaire
 * quand une fiche fusionnée porte un lien Odoo, que la fusion efface.
 *
 * Après une fusion, l'onglet recharge depuis le serveur plutôt que de rafistoler
 * l'état local : une fusion déplace les compteurs et peut dissoudre des groupes
 * voisins.
 *****************************************************************************/

/* Imports */
import { Fragment, useState } from "react";
import { t } from "i18next";
import toast from "react-hot-toast";
import { Button, Checkbox, Chip, Dialog, Radio, Typography } from "@material-tailwind/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
/***/

/* Services */
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
/***/

/**
* CustomerDuplicateGroupRow : un groupe de doublons clients
* @param group - le groupe à afficher
* @param onMerged - callback de rechargement, appelé après une fusion réussie
*/
export default function CustomerDuplicateGroupRow({
  group,
  onMerged
}: {
  group: EasyTech.CustomerDuplicateGroup,
  onMerged: () => void
}) {
  const [expanded, setExpanded] = useState(false);
  const [survivorId, setSurvivorId] = useState<string | null>(group.suggestedSurvivorId);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const history = group.members.reduce((total, member) => total + member.history, 0);
  const survivor = group.members.find((member) => member.id === survivorId) || null;
  const merged = group.members.filter(
    (member) => member.id !== survivorId && !excludedIds.includes(member.id));
  const odooLosses = merged.filter((member) => member.hasOdooLink).length;
  // survivorId est initialisé une seule fois depuis group.suggestedSurvivorId et n'est
  // jamais resynchronisé : une valeur héritée d'une forme de groupe précédente ne doit
  // jamais être acceptée, elle résoudrait survivor à null, ferait de merged la liste
  // entière et enverrait un mainCustomerId sans rapport — une fusion fausse et
  // irréversible.
  const survivorIsMember = !!survivorId && group.members.some((member) => member.id === survivorId);

  /**
  * chooseSurvivor : désigner une fiche comme survivante, en la réintégrant si elle
  * était exclue. Une fiche ne peut pas être à la fois conservée et laissée de côté.
  */
  const chooseSurvivor = (memberId: string) => {
    setSurvivorId(memberId);
    setExcludedIds((ids) => ids.filter((id) => id !== memberId));
  };

  /**
  * toggleIncluded : inclure ou exclure une fiche de cette fusion
  */
  const toggleIncluded = (memberId: string) => {
    setExcludedIds((ids) => ids.includes(memberId)
      ? ids.filter((id) => id !== memberId)
      : [...ids, memberId]);
  };

  /**
  * submitMerge : conserver la fiche choisie, retirer les autres
  */
  const submitMerge = () => {
    if (!survivorId || !group.members.some((m) => m.id === survivorId)) return;
    if (merged.length === 0) return;

    setSubmitting(true);
    api.post("admin/customers/merge", {
      mainCustomerId: survivorId,
      sourceCustomerIds: merged.map((member) => member.id)
    }).then(() => {
      toast.success(t("admin:tools.customerDedup.merge.success"));
      setConfirming(false);
      onMerged();
    }).catch((err) => {
      console.error(err);
      showApiErrorToast(err, { fallbackMessage: t("admin:tools.customerDedup.errors.merge") });
    }).finally(() => setSubmitting(false));
  };
  /***/

  return (
    <Fragment>
      <tr data-testid={"group-" + group.groupId}>
        <td>
          <button type="button"
            data-testid={"toggle-" + group.groupId}
            className="flex items-center gap-2 text-left"
            onClick={() => setExpanded((open) => !open)}>
            {expanded ? <ChevronDownIcon className="w-4" /> : <ChevronRightIcon className="w-4" />}
            <span>{group.members.map((member) => member.name).join(" · ")}</span>
          </button>
        </td>
        <td data-testid={"member-count-" + group.groupId}>{group.members.length}</td>
        <td data-testid={"history-" + group.groupId}>{history}</td>
        <td>
          <Chip size="sm"
            variant="ghost"
            color={group.requiresReview ? "amber" : "green"}
            value={t("admin:tools.customerDedup.confidence." + group.confidence)} />
        </td>
        <td>
          <Button size="sm" variant="text"
            data-testid={"open-" + group.groupId}
            onClick={() => setExpanded((open) => !open)}>
            {expanded
              ? t("admin:tools.customerDedup.hide")
              : t("admin:tools.customerDedup.view")}
          </Button>
        </td>
      </tr>

      {expanded && (
        <tr data-testid={"detail-" + group.groupId}>
          <td colSpan={5}>
            <div className="p-4 bg-blue-gray-50/40">
              {group.requiresReview && (
                <Typography variant="small" className="mb-3 text-amber-800"
                  data-testid={"review-notice-" + group.groupId}>
                  {t("admin:tools.customerDedup.reviewNotice")}
                </Typography>
              )}

              <Typography variant="small" className="mb-2 font-medium">
                {t("admin:tools.customerDedup.merge.pick")}
              </Typography>

              <ul className="flex flex-col gap-3">
                {group.members.map((member) => (
                  <li key={member.id} data-testid={"member-" + member.id}
                    className="flex flex-wrap items-start justify-between gap-4">
                    <label className="flex items-start gap-2">
                      <Radio name={"survivor-" + group.groupId}
                        data-testid={"survivor-" + member.id}
                        checked={survivorId === member.id}
                        onChange={() => chooseSurvivor(member.id)} />
                      <span className="flex flex-col">
                        <span className="flex items-center gap-2">
                          {member.name}
                          {member.hasOdooLink && (
                            <Chip size="sm" variant="ghost" color="blue"
                              data-testid={"odoo-" + member.id}
                              value={t("admin:tools.customerDedup.odooBadge")} />
                          )}
                        </span>
                        <span className="text-gray-600 text-xs">
                          {member.nip || t("admin:tools.customerDedup.noNip")}
                        </span>
                      </span>
                    </label>
                    <span className="flex items-center gap-4 text-gray-600 text-sm">
                      {/* L'historique est le nombre sur lequel l'outil classe les fiches : sans
                          lui, rien a l'ecran n'explique pourquoi le survivant propose est celui-la.
                          Cle deja presente dans les dix locales, aucune nouvelle traduction. */}
                      <span data-testid={"member-history-" + member.id}>
                        {t("admin:tools.customerDedup.table.history")} : {member.history}
                      </span>
                      <span>{t("admin:tools.customerDedup.counters.interventions", { count: member.interventions })}</span>
                      <span>{t("admin:tools.customerDedup.counters.visits", { count: member.visits })}</span>
                      <span>{t("admin:tools.customerDedup.counters.machines", { count: member.machines })}</span>
                      <span>{t("admin:tools.customerDedup.counters.contacts", { count: member.contacts })}</span>
                      {survivorId !== member.id && (
                        <label className="flex items-center gap-1 text-gray-700">
                          <Checkbox
                            data-testid={"include-" + member.id}
                            checked={!excludedIds.includes(member.id)}
                            onChange={() => toggleIncluded(member.id)} />
                          {t("admin:tools.customerDedup.merge.include")}
                        </label>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex justify-end">
                <Button size="sm" color="red"
                  data-testid={"merge-" + group.groupId}
                  disabled={!survivorIsMember || merged.length === 0 || submitting}
                  onClick={() => setConfirming(true)}>
                  {t("admin:tools.customerDedup.merge.button")}
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}

      <Dialog open={confirming} handler={() => setConfirming(false)} size="xs">
        <div className="p-4">
          <Typography variant="h5" className="mb-2">
            {t("admin:tools.customerDedup.merge.confirmTitle")}
          </Typography>
          <Typography className="mb-2">
            {t("admin:tools.customerDedup.merge.confirmBody", {
              survivor: survivor ? survivor.name : "",
              rows: merged.length
            })}
          </Typography>
          {odooLosses > 0 && (
            <Typography className="mb-4 text-amber-800"
              data-testid={"odoo-warning-" + group.groupId}>
              {t("admin:tools.customerDedup.merge.confirmOdooWarning", { count: odooLosses })}
            </Typography>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outlined" color="blue-gray" size="sm"
              data-testid={"cancel-merge-" + group.groupId}
              disabled={submitting}
              onClick={() => setConfirming(false)}>
              {t("common:cancel")}
            </Button>
            <Button color="red" size="sm"
              data-testid={"confirm-merge-" + group.groupId}
              disabled={submitting}
              onClick={submitMerge}>
              {t("admin:tools.customerDedup.merge.confirmButton")}
            </Button>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
}
/***/
